import React, { useState, useEffect, memo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import styled, { keyframes, createGlobalStyle } from 'styled-components';
import {
  toSlug, CATEGORY_STYLE, DEFAULT_STYLE, fmt,
} from '../api/services.api';
import { useServices, useSubServices, useSubServiceDetail, useCategories, toArray } from '../hooks/useCatalog';
import { useCartStore } from '../../cart/store/cartStore';
import { useToast } from '../../../shared/components/Toast/ToastProvider';
import Header from '../../../shared/components/Header';
import Footer from '../../../shared/components/Footer';
import SelectRequirementsModal from '../components/SelectRequirementsModal';
import MakeYourOwnPackageModal from '../components/MakeYourOwnPackageModal';
import BeautyVariantSection from '../components/BeautyVariantSection';
import { BEAUTY_BOOKING, MENS_BOOKING, BATHROOM_CONFIG, KITCHEN_CONFIG, HOME_CLEANING_CONFIG } from '../data/customFlows';

const PRIMARY = '#082189';

/* ─── Keyframes ─── */
const sdFadeUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const shimmer = keyframes`
  0%   { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

/* ─── Global styles: scroll reset, animation, skeleton, hover & responsive
   rules for the class-based .sd-* elements (media-query-heavy — kept as a
   single createGlobalStyle for clarity). ─── */
const SdGlobal = createGlobalStyle`
  html, body {
    overflow-x: hidden !important;
    overflow-y: auto !important;
    height: auto !important;
  }

  .sd-section { animation: ${sdFadeUp} 0.36s both; }

  .sd-skel {
    background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
    background-size: 400px 100%;
    animation: ${shimmer} 1.4s infinite;
    border-radius: 8px;
  }

  .sd-gallery-wrap { overflow: hidden; border-radius: 10px; }
  .sd-gallery-img {
    width: 100%; height: 190px; object-fit: cover; display: block;
    border-radius: 10px;
    transition: transform 0.3s cubic-bezier(0.16,1,0.3,1);
  }
  .sd-gallery-wrap:hover .sd-gallery-img { transform: scale(1.04); }

  .sd-pkg {
    border-radius: 14px; padding: 16px; position: relative;
    transition: transform 0.22s cubic-bezier(0.16,1,0.3,1), box-shadow 0.22s ease;
    cursor: pointer;
  }
  .sd-pkg:hover { transform: translateY(-2px); }

  .sd-review {
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    padding: 18px;
    box-shadow: 0 1px 4px rgba(0,0,0,0.05);
  }

  .sd-faq-item { border-bottom: 1px solid #e5e7eb; }
  .sd-faq-btn {
    width: 100%; background: none; border: none; cursor: pointer;
    display: flex; align-items: center; justify-content: space-between;
    padding: 16px 0; text-align: left;
  }
  .sd-faq-answer {
    overflow: hidden;
    transition: max-height 0.28s ease, opacity 0.28s ease;
  }

  .sd-sticky-bar {
    position: fixed; bottom: 0; left: 0; right: 0; z-index: 300;
    background: #0f172a;
    border-top: 1px solid #1e293b;
    padding: 12px 20px;
    display: flex; align-items: center; justify-content: space-between;
    gap: 12px;
    box-shadow: 0 -4px 20px rgba(0,0,0,0.25);
  }

  .sd-book-btn {
    border: none; cursor: pointer;
    transition: opacity 0.15s, transform 0.12s;
  }
  .sd-book-btn:hover { opacity: 0.9; transform: scale(1.02); }

  .sd-pkg-tab {
    border: none; cursor: pointer;
    padding: 8px 20px; border-radius: 8px;
    font-size: 13px; font-weight: 700;
    transition: all 0.18s ease;
  }

  @media (max-width: 767px) {
    .sd-hero-grid { flex-direction: column !important; }
    .sd-hero-img-wrap { flex: none !important; width: 100% !important; height: 210px !important; }
    .sd-hero-info { padding: 16px !important; }
    .sd-hero-cta { flex-direction: column !important; align-items: stretch !important; gap: 10px !important; }
    .sd-hero-cta-btn { width: 100% !important; text-align: center !important; }

    .sd-stats-bar { display: grid !important; grid-template-columns: 1fr 1fr !important; }
    .sd-stat-cell { border-right: none !important; border-bottom: 1px solid #e5e7eb !important; }
    .sd-stat-cell:nth-child(odd) { border-right: 1px solid #e5e7eb !important; }
    .sd-stat-cell:nth-child(3), .sd-stat-cell:nth-child(4) { border-bottom: none !important; }

    .sd-two-col { flex-direction: column !important; }
    .sd-col-right { order: -1 !important; }

    .sd-included-grid { grid-template-columns: 1fr !important; }

    .sd-steps-wrap     { flex-direction: column !important; gap: 0 !important; }
    .sd-step-connector { display: none !important; }
    .sd-step-item      { flex-direction: row !important; text-align: left !important; align-items: flex-start !important; gap: 14px !important; padding-bottom: 16px !important; }
    .sd-step-text      { text-align: left !important; }

    .sd-gallery-grid { grid-template-columns: 1fr 1fr !important; }
    .sd-gallery-img  { height: 120px !important; }

    .sd-review-grid { grid-template-columns: 1fr !important; }

    .sd-outer-pad     { padding: 14px 12px 12px !important; }
    .sd-section-inner { padding: 18px 14px !important; }

    .sd-back-btn  { display: none !important; }
    .sd-bar-book  { flex: 1 !important; text-align: center !important; }
    .sd-sticky-bar { padding: 10px 14px !important; }

    .sd-pkg { padding: 14px !important; }
    .sd-sec-heading { font-size: 18px !important; }
  }

  @media (max-width: 480px) {
    .sd-outer-pad     { padding: 12px 12px 10px !important; }
    .sd-section-inner { padding: 16px 14px !important; }
    .sd-hero-info     { padding: 14px !important; }
    .sd-hero-img-wrap { height: 190px !important; }
    .sd-sec-heading   { font-size: 17px !important; }

    .sd-gallery-img { height: 100px !important; }

    /* Sticky bar: keep price + action on one row without overflow. */
    .sd-sticky-bar { padding: 9px 12px !important; gap: 10px !important; }
    .sd-bar-book   { padding: 11px 16px !important; }
  }
`;

/* ─── Styled components ─── */
const Stars = memo(({ rating, size = 14 }) => (
  <StarSpan $size={size}>
    {'★'.repeat(rating)}{'☆'.repeat(5 - rating)}
  </StarSpan>
));

const StarSpan = styled.span`
  font-size: ${p => p.$size}px;
  color: #f59e0b;
  letter-spacing: 1px;
`;

const SectionHeading = memo(({ title, sub }) => (
  <SecHeadingWrap>
    <SecHeadingTitle className="sd-sec-heading">
      {title}
    </SecHeadingTitle>
    {sub ? <SecHeadingSub>{sub}</SecHeadingSub> : null}
  </SecHeadingWrap>
));

const SecHeadingWrap = styled.div`
  margin-bottom: 20px;
`;

const SecHeadingTitle = styled.h2`
  margin: 0 0 4px;
  font-size: 20px;
  font-weight: 900;
  color: #111827;
  letter-spacing: -0.3px;
`;

const SecHeadingSub = styled.p`
  margin: 0;
  font-size: 13px;
  color: #6b7280;
`;

const PkgCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
`;

const PkgName = styled.div`
  font-size: 14px;
  font-weight: 800;
  color: ${p => (p.$selected ? '#ffffff' : '#111827')};
  margin-bottom: 2px;
`;

const PkgDuration = styled.div`
  font-size: 11.5px;
  color: ${p => (p.$selected ? 'rgba(255,255,255,0.75)' : '#9ca3af')};
`;

const PkgPriceCol = styled.div`
  text-align: right;
  flex-shrink: 0;
`;

const PkgPrice = styled.div`
  font-size: 18px;
  font-weight: 900;
  color: ${p => (p.$selected ? '#ffffff' : p.$accent)};
`;

const PkgActual = styled.div`
  font-size: 11px;
  color: ${p => (p.$selected ? 'rgba(255,255,255,0.6)' : '#9ca3af')};
  text-decoration: line-through;
`;

const SubServiceCard = memo(({ sub, isSelected, accent, onSelect }) => {
  const base = sub.discountedPrice ?? sub.price ?? 0;
  const total = base + (sub.cgst ?? 0) + (sub.sgst ?? 0);
  const actual = sub.actualPrice ?? sub.originalPrice;

  return (
    <div
      className="sd-pkg"
      onClick={onSelect}
      style={{
        background: isSelected ? accent : '#f9fafb',
        border: isSelected ? `2px solid ${accent}` : '2px solid #e5e7eb',
        boxShadow: isSelected ? `0 6px 20px ${accent}28` : '0 1px 3px rgba(0,0,0,0.04)',
      }}
    >
      <PkgCardHeader>
        <div>
          <PkgName $selected={isSelected}>
            {sub.subServiceName ?? sub.name}
          </PkgName>
          {sub.duration ? (
            <PkgDuration $selected={isSelected}>
              ⏱ {sub.duration}
            </PkgDuration>
          ) : null}
        </div>
        <PkgPriceCol>
          {total > 0 ? (
            <PkgPrice $selected={isSelected} $accent={accent}>
              {fmt(total)}
            </PkgPrice>
          ) : null}
          {actual != null && actual !== base ? (
            <PkgActual $selected={isSelected}>
              {fmt(actual)}
            </PkgActual>
          ) : null}
        </PkgPriceCol>
      </PkgCardHeader>
    </div>
  );
});

/* ─── Layout / section styled components ─── */
const ErrorWrap = styled.div`
  min-height: 60vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  font-family: 'Inter', sans-serif;
  padding: 20px;
`;

const ErrorIcon = styled.span`
  font-size: 44px;
`;

const ErrorTitle = styled.p`
  font-weight: 800;
  font-size: 22px;
  color: #111827;
  margin: 0;
`;

const ErrorMsg = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0;
`;

const RetryBtn = styled.button`
  padding: 10px 24px;
  border-radius: 8px;
  background: ${PRIMARY};
  color: #fff;
  font-weight: 700;
  font-size: 14px;
  border: none;
  cursor: pointer;
`;

const Page = styled.div`
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  background: #f3f4f6;
  min-height: 100vh;
  padding-bottom: 90px;
`;

const AccentStrip = styled.div`
  height: 3px;
  background: linear-gradient(90deg, ${p => p.$from}, ${p => p.$to});
`;

const BreadcrumbBar = styled.div`
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
`;

const BreadcrumbInner = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 11px 16px;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`;

const Crumb = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #6b7280;
  font-size: 13px;
  font-weight: 500;
  padding: 0;
`;

const CrumbSep = styled.span`
  color: #d1d5db;
`;

const CrumbCurrent = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: #111827;
`;

const OuterPad = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 24px 20px;
`;

const HeroSkelCard = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  overflow: hidden;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  display: flex;
  height: 330px;
`;

const HeroSkelBody = styled.div`
  flex: 1;
  padding: 26px;
`;

const HeroCard = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  gap: 0;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
`;

const HeroImgWrap = styled.div`
  flex: 0 0 42%;
  height: 330px;
  overflow: hidden;
  position: relative;
`;

const HeroImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const HeroInfo = styled.div`
  flex: 1;
  padding: 26px 26px 22px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-width: 0;
`;

const HeroEyebrow = styled.p`
  margin: 0 0 5px;
  font-size: 11.5px;
  font-weight: 700;
  color: ${p => p.$accent};
  text-transform: uppercase;
  letter-spacing: 0.7px;
`;

const HeroTitle = styled.h1`
  margin: 0 0 9px;
  font-size: clamp(19px, 3vw, 25px);
  font-weight: 900;
  color: #111827;
  letter-spacing: -0.4px;
  line-height: 1.2;
`;

const HeroDesc = styled.p`
  margin: 0 0 14px;
  font-size: 13.5px;
  color: #4b5563;
  line-height: 1.65;
`;

const HeroRatingRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
`;

const HeroRatingValue = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: #111827;
`;

const HeroRatingCount = styled.span`
  font-size: 12.5px;
  color: #9ca3af;
`;

const HeroChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  margin-bottom: 18px;
`;

const Chip = styled.span`
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 20px;
  padding: 4px 11px;
  font-size: 12px;
  font-weight: 600;
  color: #374151;
`;

const HeroCtaRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const HeroCtaPrice = styled.div`
  display: flex;
  align-items: baseline;
  gap: 7px;
`;

const HeroPrice = styled.span`
  font-size: 30px;
  font-weight: 900;
  color: ${p => p.$accent};

  @media (max-width: 480px) {
    font-size: 25px;
  }
`;

const HeroPriceNote = styled.span`
  font-size: 11.5px;
  color: #9ca3af;
`;

const BookBtn = styled.button`
  padding: ${p => p.$padding || '11px 26px'};
  border-radius: ${p => p.$radius || 10}px;
  background: ${p => (p.$booked ? '#15803d' : PRIMARY)};
  color: #ffffff;
  font-size: 14px;
  font-weight: 800;
  ${p => (p.$fullWidth ? 'width: 100%; margin-top: 14px;' : '')}
`;

const OutlineBtn = styled.button`
  width: 100%;
  margin-top: ${p => (p.$first ? '10px' : '8px')};
  padding: 11px 14px;
  border-radius: 10px;
  background: #ffffff;
  border: 1.5px solid ${PRIMARY};
  color: ${PRIMARY};
  font-size: 13.5px;
  font-weight: 800;
  cursor: pointer;
  transition: background 0.15s, transform 0.12s;

  &:hover { background: ${PRIMARY}0d; transform: scale(1.01); }
`;

const StatsBar = styled.div`
  display: flex;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 16px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.04);
  animation-delay: 0.06s;
`;

const StatCell = styled.div`
  flex: 1;
  padding: 13px 8px;
  text-align: center;
  border-right: ${p => (p.$last ? 'none' : '1px solid #e5e7eb')};
`;

const StatIcon = styled.div`
  font-size: 15px;
  margin-bottom: 2px;
`;

const StatValue = styled.div`
  font-size: 13px;
  font-weight: 800;
  color: #111827;
  line-height: 1;
`;

const StatLabel = styled.div`
  font-size: 10px;
  color: #9ca3af;
  font-weight: 500;
  margin-top: 3px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
`;

const TwoCol = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-start;
  flex-wrap: wrap;
`;

const ColLeft = styled.div`
  flex: 1 1 55%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;

  ${p => p.$plain ? `
    /* Full-page scroll (beauty) — no independent scroll pane. */
    position: static;
    max-height: none;
    overflow: visible;
    padding-right: 0;
  ` : `
    /* Independent scroll pane — the left content scrolls on its own. */
    position: sticky;
    top: 76px;
    align-self: flex-start;
    max-height: calc(100vh - 92px);
    overflow-y: auto;
    padding-right: 4px;
    scrollbar-width: thin;
    &::-webkit-scrollbar { width: 6px; }
    &::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 4px; }

    @media (max-width: 767px) {
      position: static;
      max-height: none;
      overflow: visible;
      padding-right: 0;
    }
  `}
`;

const ColRight = styled.div`
  flex: 0 1 37%;
  min-width: 260px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  /* Fixed (sticky) right column — only the left column scrolls. If the
     sidebar itself is taller than the viewport it scrolls internally. */
  position: sticky;
  top: 76px;
  align-self: flex-start;
  max-height: calc(100vh - 92px);
  overflow-y: auto;
  /* Hide the scrollbar on the right column while keeping it scrollable. */
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar { display: none; }

  @media (max-width: 767px) {
    position: static;
    max-height: none;
    overflow: visible;
  }
`;

const SectionCard = styled.div`
  background: ${p => p.$bg || '#ffffff'};
  border: 1px solid ${p => p.$border || '#e5e7eb'};
  border-radius: 14px;
  padding: 22px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.04);
  ${p => (p.$delay != null ? `animation-delay: ${p.$delay}s;` : '')}
`;

const Grid2 = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 9px 14px;
`;

const SkelRow = styled.div`
  display: flex;
  gap: 9px;
`;

const IncludedItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 9px;
`;

const CheckBadge = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${p => p.$bg};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 2px;
`;

const CheckMark = styled.span`
  color: #ffffff;
  font-size: 11px;
  font-weight: 800;
`;

const IncludedText = styled.span`
  font-size: 13px;
  color: ${p => p.$color || '#374151'};
  line-height: 1.5;
`;

const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Tag = styled.span`
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 20px;
  padding: 5px 13px;
  font-size: 12px;
  font-weight: 600;
  color: #374151;
`;

const PrereqList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PrereqItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 9px;
`;

const PrereqArrow = styled.span`
  font-size: 13px;
  color: #15803d;
  font-weight: 700;
  flex-shrink: 0;
`;

const PrereqText = styled.span`
  font-size: 13px;
  color: #14532d;
  line-height: 1.5;
`;

const StepsWrap = styled.div`
  display: flex;
  gap: 0;
  align-items: flex-start;
`;

const StepItem = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 8px;
`;

const StepNum = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 800;
  background: ${p => p.$accent};
  color: #ffffff;
`;

const StepTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #111827;
`;

const StepDesc = styled.div`
  font-size: 12px;
  color: #6b7280;
  line-height: 1.5;
  margin-top: 3px;
`;

const StepConnector = styled.div`
  width: 28px;
  height: 2px;
  background: #e5e7eb;
  margin-top: 17px;
  flex-shrink: 0;
`;

const ReviewSummary = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 14px 16px;
  margin-bottom: 18px;
`;

const ReviewScoreCol = styled.div`
  text-align: center;
  flex-shrink: 0;
`;

const ReviewScore = styled.div`
  font-size: 34px;
  font-weight: 900;
  color: #111827;
  line-height: 1;
`;

const ReviewOutOf = styled.div`
  font-size: 10.5px;
  color: #9ca3af;
  margin-top: 2px;
`;

const ReviewBars = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const ReviewBarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
`;

const ReviewBarStarNum = styled.span`
  font-size: 11px;
  color: #6b7280;
  width: 10px;
`;

const ReviewBarStar = styled.span`
  font-size: 10px;
  color: #f59e0b;
`;

const ReviewBarTrack = styled.div`
  flex: 1;
  height: 5px;
  background: #e5e7eb;
  border-radius: 4px;
  overflow: hidden;
`;

const ReviewBarFill = styled.div`
  height: 100%;
  border-radius: 4px;
  background: ${p => p.$accent};
  width: ${p => p.$width};
`;

const ReviewBarPct = styled.span`
  font-size: 10.5px;
  color: #9ca3af;
  width: 26px;
`;

const ReviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
  gap: 12px;
`;

const ReviewHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const ReviewAuthor = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
`;

const ReviewAvatar = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${p => p.$accent};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 800;
  color: #ffffff;
`;

const ReviewName = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #111827;
`;

const ReviewDate = styled.div`
  font-size: 11px;
  color: #9ca3af;
`;

const ReviewText = styled.p`
  margin: 0;
  font-size: 13px;
  color: #4b5563;
  line-height: 1.6;
`;

const FaqQ = styled.span`
  font-size: 13.5px;
  font-weight: 700;
  color: #111827;
  padding-right: 14px;
  line-height: 1.4;
`;

const FaqToggle = styled.span`
  font-size: 18px;
  font-weight: 700;
  flex-shrink: 0;
  color: ${p => (p.$open ? p.$accent : '#9ca3af')};
  transition: transform 0.22s ease, color 0.18s;
  transform: ${p => (p.$open ? 'rotate(45deg)' : 'rotate(0deg)')};
  display: inline-block;
`;

const FaqAnswer = styled.div.attrs(p => ({
  style: {
    maxHeight: p.$open ? 220 : 0,
    opacity: p.$open ? 1 : 0,
  },
}))`
  overflow: hidden;
  transition: max-height 0.28s ease, opacity 0.28s ease;
`;

const FaqAnswerText = styled.p`
  margin: 0 0 16px;
  font-size: 13px;
  color: #6b7280;
  line-height: 1.65;
`;

const PlanCard = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 22px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.04);
  animation-delay: 0.1s;
`;

const PkgTabs = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 14px;
  background: #f3f4f6;
  border-radius: 10px;
  padding: 4px;
`;

const ColumnList = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
  overflow-x: auto;
  padding-bottom: 6px;
  scrollbar-width: thin;
  &::-webkit-scrollbar { height: 6px; }
  &::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 4px; }

  /* Each plan sits in its own fixed-width column and scrolls horizontally. */
  & > * {
    flex: 0 0 auto;
    min-width: 168px;
  }
`;

const NoPlans = styled.div`
  text-align: center;
  padding: 20px 0;
  color: #9ca3af;
  font-size: 13px;
`;

const TrustBox = styled.div`
  margin-top: 14px;
  padding: 12px;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
`;

const TrustRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: ${p => (p.$last ? 0 : 9)}px;
`;

const TrustIcon = styled.span`
  font-size: 13px;
`;

const TrustText = styled.span`
  font-size: 12px;
  color: #374151;
  font-weight: 500;
  line-height: 1.4;
`;

const FactsCard = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 20px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.04);
  animation-delay: 0.16s;
`;

const FactsTitle = styled.div`
  font-size: 14px;
  font-weight: 800;
  color: #111827;
  margin-bottom: 12px;
`;

const FactRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 7px 0;
  border-bottom: ${p => (p.$last ? 'none' : '1px solid #f3f4f6')};
`;

const FactLabel = styled.span`
  font-size: 12.5px;
  color: #9ca3af;
  font-weight: 500;
`;

const FactValue = styled.span`
  font-size: 12.5px;
  color: #111827;
  font-weight: 700;
`;

const StickyLabel = styled.div`
  font-size: 11px;
  color: #94a3b8;
  font-weight: 500;
  margin-bottom: 1px;
  max-width: 46vw;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StickyPriceRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 7px;
`;

const StickyPrice = styled.span`
  font-size: 22px;
  font-weight: 900;
  color: #ffffff;
`;

const StickyActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const StickyBackBtn = styled.button`
  padding: 10px 16px;
  border-radius: 8px;
  background: #1e293b;
  color: #94a3b8;
  font-size: 13px;
  font-weight: 600;
  border: none;
  cursor: pointer;
`;

const StickyBookBtn = styled.button`
  padding: 11px 26px;
  border-radius: 8px;
  background: ${p => (p.$booked ? '#15803d' : PRIMARY)};
  color: #ffffff;
  font-size: 14px;
  font-weight: 800;
`;

const FALLBACK = {
  steps: [
    { title: 'Book Online',    desc: 'Choose your slot and confirm in minutes' },
    { title: 'Expert Arrives', desc: 'Verified professional at your doorstep' },
    { title: 'Service Done',   desc: 'Quality work with full satisfaction' },
    { title: 'Pay After',      desc: 'Pay only after you are happy with the service' },
  ],
  gallery: [
    'https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=400&h=250&fit=crop',
    'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=400&h=250&fit=crop',
    'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=250&fit=crop',
    'https://images.unsplash.com/photo-1585771724684-38269d6639fd?w=400&h=250&fit=crop',
    'https://images.unsplash.com/photo-1527515637462-cff94eecc1ac?w=400&h=250&fit=crop',
    'https://images.unsplash.com/photo-1521590832167-7bcbfaa6381f?w=400&h=250&fit=crop',
  ],
  reviews: [
    { name: 'Priya M.',  rating: 5, date: 'Apr 2025', text: 'Excellent service! The professional was on time and very thorough.' },
    { name: 'Rahul S.',  rating: 5, date: 'Apr 2025', text: 'Great experience. Very professional and quality was top-notch.' },
    { name: 'Anita K.',  rating: 4, date: 'Mar 2025', text: 'Good service overall. Will definitely book again.' },
    { name: 'Suresh R.', rating: 5, date: 'Mar 2025', text: 'Absolutely fantastic. City Craft never disappoints!' },
  ],
  faqs: [
    { q: 'How do I book this service?',          a: 'Simply tap Book Now, select your preferred time slot, and confirm.' },
    { q: 'What if I am not satisfied?',          a: 'We offer a free re-service within 7 days if you are not fully satisfied.' },
    { q: 'Do I need to provide any materials?',  a: 'No. Our professionals bring all the required equipment and products.' },
    { q: 'How are your professionals verified?', a: 'All City Craft professionals undergo background checks and skill assessments.' },
    { q: 'When do I pay?',                       a: 'You pay only after the service is completed — no advance payment required.' },
  ],
};

/* ─── Static presentational content for the added visual sections ─── */
const PROCESS_STEPS = [
  { step: 'STEP 01', title: 'Foam Application', desc: 'Lifts stubborn dirt & stains', img: 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=500&h=300&fit=crop' },
  { step: 'STEP 02', title: 'Power Scrubbing', desc: 'Removes layers of built-up grime', img: 'https://images.unsplash.com/photo-1527515637462-cff94eecc1ac?w=500&h=300&fit=crop' },
  { step: 'STEP 03', title: 'Sanitize & Disinfect', desc: 'Kills 99.9% of germs & bacteria', img: 'https://images.unsplash.com/photo-1585421514738-01798e348b17?w=500&h=300&fit=crop' },
  { step: 'STEP 04', title: 'Finishing Touch', desc: 'Streak-free shine, fully dried', img: 'https://images.unsplash.com/photo-1563453392212-326f5e854473?w=500&h=300&fit=crop' },
];

const PROCESS_FALLBACK_IMG = PROCESS_STEPS[0].img;

// 8 compact before/after comparison tiles for the "See the difference" grid.
const BEFORE_AFTER_ITEMS = [
  { before: 'https://images.unsplash.com/photo-1584622650111-993a426fbf0a?w=400&h=300&fit=crop', after: 'https://images.unsplash.com/photo-1600585152220-90363fe7e115?w=400&h=300&fit=crop' },
  { before: 'https://images.unsplash.com/photo-1585421514738-01798e348b17?w=400&h=300&fit=crop', after: 'https://images.unsplash.com/photo-1595428774223-ef52624120d2?w=400&h=300&fit=crop' },
  { before: 'https://images.unsplash.com/photo-1527515637462-cff94eecc1ac?w=400&h=300&fit=crop', after: 'https://images.unsplash.com/photo-1556911220-e15b29be8c8f?w=400&h=300&fit=crop' },
  { before: 'https://images.unsplash.com/photo-1563453392212-326f5e854473?w=400&h=300&fit=crop', after: 'https://images.unsplash.com/photo-1600566752355-35792bedcfea?w=400&h=300&fit=crop' },
  { before: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=300&fit=crop', after: 'https://images.unsplash.com/photo-1600210492486-724fe5c67fb3?w=400&h=300&fit=crop' },
  { before: 'https://images.unsplash.com/photo-1596263576925-d7a1a7e0f8e9?w=400&h=300&fit=crop', after: 'https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?w=400&h=300&fit=crop' },
  { before: 'https://images.unsplash.com/photo-1521207418485-99c705420785?w=400&h=300&fit=crop', after: 'https://images.unsplash.com/photo-1493809842364-78817add7ffb?w=400&h=300&fit=crop' },
  { before: 'https://images.unsplash.com/photo-1584622781564-1d987f7333c1?w=400&h=300&fit=crop', after: 'https://images.unsplash.com/photo-1502005229762-cf1b2da7c5d6?w=400&h=300&fit=crop' },
];

const EQUIPMENT = [
  { name: 'Buffing Machine', img: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=300&h=300&fit=crop' },
  { name: 'Steam Cleaner', img: 'https://images.unsplash.com/photo-1585421514738-01798e348b17?w=300&h=300&fit=crop' },
  { name: 'Disinfectants', img: 'https://images.unsplash.com/photo-1563453392212-326f5e854473?w=300&h=300&fit=crop' },
  { name: 'Microfiber Cloths', img: 'https://images.unsplash.com/photo-1596263576925-d7a1a7e0f8e9?w=300&h=300&fit=crop' },
  { name: 'Vacuum', img: 'https://images.unsplash.com/photo-1581578017426-8c6f7e3f9b1a?w=300&h=300&fit=crop' },
  { name: 'Safety Gear', img: 'https://images.unsplash.com/photo-1527515637462-cff94eecc1ac?w=300&h=300&fit=crop' },
];

const EQUIPMENT_FALLBACK_IMG = EQUIPMENT[0].img;

const REQUIREMENTS = [
  { icon: '🪣', label: 'A Bucket' },
  { icon: '🔌', label: 'Power Outlet' },
  { icon: '🪜', label: 'A Ladder' },
  { icon: '🚰', label: 'Water Supply' },
];

/* ─── Styled components for the 4 added visual sections ─── */
const ProcessGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }
`;

const ProcessCard = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0,0,0,0.05);
  transition: transform 0.22s cubic-bezier(0.16,1,0.3,1), box-shadow 0.22s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 22px rgba(8,33,137,0.12);
  }
`;

const ProcessImgWrap = styled.div`
  position: relative;
  height: 150px;
  overflow: hidden;
`;

const ProcessImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  border-top-left-radius: 14px;
  border-top-right-radius: 14px;
`;

const ProcessBadge = styled.span`
  position: absolute;
  top: 10px;
  left: 10px;
  background: ${PRIMARY};
  color: #ffffff;
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.5px;
  padding: 4px 10px;
  border-radius: 20px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.2);
`;

const ProcessBody = styled.div`
  padding: 14px 16px 16px;
`;

const ProcessTitle = styled.div`
  font-size: 14px;
  font-weight: 800;
  color: #111827;
  margin-bottom: 3px;
`;

const ProcessDesc = styled.div`
  font-size: 12.5px;
  color: #6b7280;
  line-height: 1.5;
`;

const DiffGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 14px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

const DiffCard = styled.div`
  position: relative;
  display: flex;
  height: 160px;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  transition: transform 0.25s ease, box-shadow 0.25s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 22px rgba(0, 0, 0, 0.1);
  }

  @media (max-width: 600px) {
    height: 150px;
  }
`;

const DiffHalf = styled.div.attrs(p => ({
  style: { backgroundImage: `url(${p.$img})` },
}))`
  flex: 1;
  background-size: cover;
  background-position: center;
  ${p => (p.$side === 'left' ? 'border-right: 2px solid #ffffff;' : '')}
`;

const DiffTag = styled.span`
  position: absolute;
  bottom: 8px;
  ${p => (p.$side === 'left' ? 'left: 8px;' : 'right: 8px;')}
  background: rgba(15, 23, 42, 0.82);
  color: #ffffff;
  font-size: 9.5px;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 20px;
  letter-spacing: 0.3px;
`;

const EquipGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;

  @media (max-width: 767px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const EquipCard = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0,0,0,0.05);
  text-align: center;
  transition: transform 0.22s cubic-bezier(0.16,1,0.3,1);

  &:hover { transform: translateY(-2px); }
`;

const EquipImg = styled.img`
  width: 100%;
  height: 110px;
  object-fit: cover;
  display: block;
`;

const EquipName = styled.div`
  font-size: 12.5px;
  font-weight: 700;
  color: #111827;
  padding: 10px 8px;
`;

const ReqGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;

  @media (max-width: 767px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const ReqCard = styled.div`
  display: flex;
  align-items: center;
  gap: 11px;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 12px;
  padding: 12px 14px;
`;

const ReqIconCircle = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${PRIMARY}14;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
`;

const ReqLabel = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: #14532d;
  line-height: 1.3;
`;

const ServiceDetailPage = () => {
  const { category, subCategoryID, serviceID } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const addItem = useCartStore(s => s.addItem);
  const toast = useToast();

  const [activePkgId, setActivePkgId] = useState(null);
  const [selectedSubId, setSelectedSubId] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);
  const [booked, setBooked] = useState(false);
  const [reqOpen, setReqOpen] = useState(false);
  const [pkgOpen, setPkgOpen] = useState(false);
  const [bathroomOpen, setBathroomOpen] = useState(false);
  const [kitchenOpen, setKitchenOpen] = useState(false);

  // ── Data layer (React Query) ──
  const { data: catsData, error: catsError } = useCategories();
  const {
    data: svcData,
    isLoading: svcQueryLoading,
    error: svcQueryError,
  } = useServices(subCategoryID);



  const cats = toArray(catsData);
  // GetServices returns { subCategoryName, services: [] } directly.
  const apiSubCatName = svcData?.subCategoryName ?? '';
  const svcList = toArray(svcData);
  const serviceObj = svcList.find(s => String(s.serviceID) === String(serviceID)) ?? null;
  const packages = Array.isArray(serviceObj?.packages) ? serviceObj.packages : [];

  // Resolve the active package: URL ?pkg= wins, else first package.
  const pkgFromUrl = searchParams.get('pkg') ? Number(searchParams.get('pkg')) : null;
  useEffect(() => {
    const firstPkgId = packages[0]?.packageID ?? null;
    setActivePkgId(pkgFromUrl ?? firstPkgId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceID, subCategoryID, packages.length]);

  // Sub-services for the active package.
  const {
    data: subSvcData,
    isFetching: subSvcFetching,
  } = useSubServices(serviceID, activePkgId);
  const subServices = toArray(subSvcData);

  // Default-select the first sub-service whenever the list changes.
  useEffect(() => {
    setSelectedSubId(subServices[0]?.subServiceID ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceID, activePkgId, subServices.length]);

  const selectedSub = subServices.find(s => s.subServiceID === selectedSubId) ?? null;

  // Detail for the selected sub-service.
  const {
    data: detail,
    isFetching: detailLoading,
  } = useSubServiceDetail(selectedSub?.subServiceID);

  // Reset UI-only state when navigating to a different service.
  useEffect(() => {
    document.body.style.overflow = '';
    document.documentElement.style.overflow = '';
    document.body.style.height = '';
    document.documentElement.style.height = '';
    window.scrollTo({ top: 0 });
    setBooked(false);
    setOpenFaq(null);
  }, [serviceID, subCategoryID]);

  const loading = svcQueryLoading;
  const error = (catsError || svcQueryError) ? (catsError?.message ?? svcQueryError?.message) : null;
  const subSvcLoading = subSvcFetching;

  const handlePkgChange = (pkgId) => {
    if (pkgId === activePkgId) return;
    setSelectedSubId(null);
    setActivePkgId(pkgId);
  };

  const style = CATEGORY_STYLE[category] || DEFAULT_STYLE;
  const accent = PRIMARY;

  const catData = cats.find(c => toSlug(c.categoryName ?? c.name ?? '') === category);
  const catName = catData?.categoryName ?? catData?.name ?? category.replace(/-/g, ' ');
  const subCatName = apiSubCatName ||
    (((catData?.subCategories ?? catData?.subcategories ?? [])
      .find(s => String(s.subCategoryID) === String(subCategoryID))?.subCategoryName) ?? 'Service');

  const serviceName = serviceObj?.serviceName ?? serviceObj?.name ?? 'Service';
  const serviceImage = serviceObj?.imageURL || serviceObj?.imagePath || FALLBACK.gallery[0];
  const hasMultiplePackages = packages.length > 1;

  const inclusions    = Array.isArray(detail?.inclusions)    ? detail.inclusions    : [];
  const exclusions    = Array.isArray(detail?.exclusions)    ? detail.exclusions    : [];
  const products      = Array.isArray(detail?.productsUsed)  ? detail.productsUsed  :
                         Array.isArray(detail?.products)      ? detail.products      : [];
  const prerequisites = Array.isArray(detail?.preRequisites) ? detail.preRequisites :
                         Array.isArray(detail?.prerequisites) ? detail.prerequisites : [];
  const description   = detail?.description ?? serviceObj?.description ?? '';

  const calcTotal = (sub) => {
    if (!sub) return null;
    const base = sub.discountedPrice ?? sub.price ?? 0;
    return base + (sub.cgst ?? 0) + (sub.sgst ?? 0);
  };
  const activeTotal = calcTotal(selectedSub);
  const activePrice = activeTotal != null ? fmt(activeTotal) : null;

  const displayDuration = selectedSub?.duration ?? serviceObj?.duration ?? '45 mins';
  const displayRating   = selectedSub?.averageRating != null
    ? Number(selectedSub.averageRating).toFixed(1)
    : '4.8';

  const isSvcLoading = loading || subSvcLoading;

  // Context passed to the customization modals.
  const modalCtx = {
    serviceID,
    subCategoryID,
    categoryName: catName,
    categorySlug: category,
  };

  // Subcategories that use the Urban-Company-style requirements configurator.
  const requiresConfig = ['Full Home Cleaning'].includes(apiSubCatName);

  // One specific service (/home-cleaning/7/55) uses the focused "how many
  // bathrooms?" flow: only the first 2 plan items are shown, and tapping any
  // of them opens a radio popup to pick the bathroom count.
  const isBathroomFlow =
    String(subCategoryID) === '7' && String(serviceID) === '55';

  // Another specific service (/home-cleaning/38/56) uses the Urban-Company-style
  // "Complete kitchen cleaning" requirements flow (cabinet + appliances + plan).
  const isKitchenFlow =
    String(subCategoryID) === '38' && String(serviceID) === '56';

  // "Deep Bathroom Cleaning" hides the "Choose a Plan" sidebar entirely.
  const isDeepBathroom = /deep\s*bathroom\s*cleaning/i.test(serviceName || '');

  const displayedSubs = isBathroomFlow ? subServices.slice(0, 2) : subServices;

  // Categories that use the inline "choose a variant" flow (beauty + men's care).
  // Detected from the API categoryName, with URL slug + resolved name fallbacks.
  const apiCategoryName = subSvcData?.categoryName ?? svcData?.categoryName ?? '';
  const isBeauty =
    /glam\s*&?\s*glow/i.test(apiCategoryName) ||
    category === 'glam-glow' ||
    /glam\s*&?\s*glow/i.test(catName || '');
  const isMens =
    /men'?s\s*care/i.test(apiCategoryName) ||


    
    category === 'mens-care' ||
    /men'?s\s*care/i.test(catName || '');
  const isStyled = isBeauty || isMens;

  // Which variant set + rating to show in the inline section.
  const bookingData = isMens ? MENS_BOOKING : BEAUTY_BOOKING;

  // One specific service (/home-cleaning/9/64) hides the plan section entirely
  // and drops the "Count of bathrooms" group from the requirements modal.
  const isNoPlan = String(subCategoryID) === '9' && String(serviceID) === '64';

  const reqConfig = isNoPlan
    ? { ...HOME_CLEANING_CONFIG, groups: HOME_CLEANING_CONFIG.groups.filter(g => g.id !== 'bathroom') }
    : HOME_CLEANING_CONFIG;

  // Regular services (no special flow) render their plans as the same
  // horizontal add-on cards used for beauty — image, rating, price, Add.
  const isRegularPlan =
    !isStyled && !isKitchenFlow && !isDeepBathroom && !isBathroomFlow && !isNoPlan &&
    subServices.length > 0;

  const planVariants = subServices.map((s, i) => {
    const base = (s.discountedPrice ?? s.price ?? 0) + (s.cgst ?? 0) + (s.sgst ?? 0);
    return {
      id: String(s.subServiceID ?? i),
      name: s.subServiceName ?? s.name ?? 'Plan',
      price: base,
      rating: s.averageRating != null ? Number(s.averageRating).toFixed(2) : displayRating,
      reviews: s.totalReviews ?? s.ratingCount ?? '2K',
      image: s.imageURL || serviceImage,
      badge: s.discountPercentage > 0 ? `${Math.round(s.discountPercentage)}% OFF` : undefined,
    };
  });

  // "Book Now": for configurator subcategories, open Select Requirements
  // (which then routes to checkout); otherwise add the selected plan to cart.
  const handleBookNow = () => {
    if (isBathroomFlow) { setBathroomOpen(true); return; }
    if (isKitchenFlow) { setKitchenOpen(true); return; }
    if (requiresConfig) { setReqOpen(true); return; }
    handleAddToCart();
  };

  // ── Add to Cart (replaces the old local-only "Book Now" toggle) ──
  const handleAddToCart = () => {
    if (!selectedSub) return;
    addItem({
      subServiceID: selectedSub.subServiceID,
      subServiceName: selectedSub.subServiceName ?? selectedSub.name,
      quantity: 1,
      price: selectedSub.discountedPrice ?? selectedSub.price ?? 0,
      cgst: selectedSub.cgst ?? 0,
      sgst: selectedSub.sgst ?? 0,
      imageURL: selectedSub.imageURL || serviceObj?.imageURL || serviceObj?.imagePath,
      serviceID,
      subCategoryID,
      categoryName: catName,
      categorySlug: category,
      packageID: activePkgId,
    });
    setBooked(true);
    toast.success('Added to cart');
    navigate('/cart');
  };

  if (error) {
    return (
      <>
        <SdGlobal />
        <Header />
        <ErrorWrap>
          <ErrorIcon>⚠️</ErrorIcon>
          <ErrorTitle>Failed to load service</ErrorTitle>
          <ErrorMsg>{error}</ErrorMsg>
          <RetryBtn onClick={() => window.location.reload()}>
            Try Again
          </RetryBtn>
        </ErrorWrap>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SdGlobal />
      <Header />

      <Page>
        <AccentStrip $from={style.from} $to={style.to} />

        <BreadcrumbBar>
          <BreadcrumbInner>
            <Crumb onClick={() => navigate('/')}>Home</Crumb>
            <CrumbSep>›</CrumbSep>
            <Crumb onClick={() => navigate(`/${category}`)}>{catName}</Crumb>
            <CrumbSep>›</CrumbSep>
            <Crumb onClick={() => navigate(`/${category}/${subCategoryID}`)}>{subCatName}</Crumb>
            <CrumbSep>›</CrumbSep>
            <CrumbCurrent>{serviceName}</CrumbCurrent>
          </BreadcrumbInner>
        </BreadcrumbBar>

        <OuterPad className="sd-outer-pad">

          {loading ? (
            <HeroSkelCard className="sd-section">
              <div className="sd-skel" style={{ flex: '0 0 42%' }} />
              <HeroSkelBody>
                <div className="sd-skel" style={{ height: 12, width: '40%', marginBottom: 14 }} />
                <div className="sd-skel" style={{ height: 28, width: '80%', marginBottom: 10 }} />
                <div className="sd-skel" style={{ height: 14, marginBottom: 6 }} />
                <div className="sd-skel" style={{ height: 14, width: '70%', marginBottom: 24 }} />
                <div className="sd-skel" style={{ height: 36, width: 120, borderRadius: 10 }} />
              </HeroSkelBody>
            </HeroSkelCard>
          ) : serviceObj ? (
            <HeroCard className="sd-section sd-hero-grid">
              <HeroImgWrap className="sd-hero-img-wrap">
                <HeroImg src={serviceImage} alt={serviceName} onError={e => { e.target.src = FALLBACK.gallery[0]; }} />
              </HeroImgWrap>

              <HeroInfo className="sd-hero-info">
                <div>
                  <HeroEyebrow $accent={accent}>
                    {style.icon} {catName} · {subCatName}
                  </HeroEyebrow>
                  <HeroTitle>
                    {serviceName}
                  </HeroTitle>
                  {description ? (
                    <HeroDesc
                      dangerouslySetInnerHTML={{ __html: description }}
                    />
                  ) : null}

                  <HeroRatingRow>
                    <Stars rating={Math.round(Number(displayRating))} size={15} />
                    <HeroRatingValue>{displayRating}</HeroRatingValue>
                    <HeroRatingCount>({subServices.length > 0 ? `${subServices.length} reviews` : '25,000+ bookings'})</HeroRatingCount>
                  </HeroRatingRow>

                  <HeroChips>
                    {[
                      { icon: '⏱', text: displayDuration },
                      { icon: '✓', text: 'Guaranteed' },
                      // { icon: '🔄', text: 'Free re-service' },
                    ].map((chip, i) => (
                      <Chip key={i}>
                        {chip.icon} {chip.text}
                      </Chip>
                    ))}
                  </HeroChips>
                </div>

                <HeroCtaRow className="sd-hero-cta">
                  <HeroCtaPrice>
                    <HeroPrice $accent={accent}>{activePrice || 'View Plans'}</HeroPrice>
                    <HeroPriceNote>{activePrice ? 'onwards' : ''}</HeroPriceNote>
                  </HeroCtaPrice>
                  {!isStyled && (
                    <BookBtn
                      className="sd-book-btn sd-hero-cta-btn"
                      onClick={handleBookNow}
                      $booked={booked}
                    >
                      {booked ? '✓ Booked!' : 'Book Now →'}
                    </BookBtn>
                  )}
                </HeroCtaRow>
              </HeroInfo>
            </HeroCard>
          ) : null}

          

          {isStyled && (
            <div style={{ margin: '0 0 16px' }}>
              <BeautyVariantSection
                serviceName={serviceName}
                ratingLabel={bookingData.ratingLabel}
                variants={bookingData.variants}
                ctx={modalCtx}
                destination="/checkout"
              />
            </div>
          )}

          {isRegularPlan && (
            <div style={{ margin: '0 0 16px' }}>
              <BeautyVariantSection
                serviceName={serviceName}
                ratingLabel={`${displayRating} (${subServices.length} option${subServices.length === 1 ? '' : 's'})`}
                variants={planVariants}
                ctx={modalCtx}
                destination="/cart"
              />
            </div>
          )}

          <TwoCol className="sd-two-col">

            <ColLeft $plain={isStyled || isKitchenFlow || isDeepBathroom || isBathroomFlow || isRegularPlan || isNoPlan}>

              <SectionCard className="sd-section sd-section-inner" $delay={0.1}>
                <SectionHeading title="What's Included" sub="Everything covered in this service" />
                {detailLoading ? (
                  <Grid2>
                    {Array.from({ length: 6 }).map((_, i) => (
                      <SkelRow key={i}>
                        <div className="sd-skel" style={{ width: 20, height: 20, borderRadius: '50%', flexShrink: 0 }} />
                        <div className="sd-skel" style={{ height: 14, flex: 1 }} />
                      </SkelRow>
                    ))}
                  </Grid2>
                ) : (
                  <Grid2 className="sd-included-grid">
                    {(inclusions.length > 0 ? inclusions : [
                      'Professional equipment & products',
                      'Trained & verified technician',
                      'Before & after cleanup',
                      'Safety standards followed',
                      'Quality assurance check',
                      'Customer satisfaction guarantee',
                    ]).map((item, i) => (
                      <IncludedItem key={i}>
                        <CheckBadge $bg={accent}>
                          <CheckMark>✓</CheckMark>
                        </CheckBadge>
                        <IncludedText>{item}</IncludedText>
                      </IncludedItem>
                    ))}
                  </Grid2>
                )}
              </SectionCard>

              <SectionCard className="sd-section sd-section-inner" $bg="#fffbeb" $border="#fde68a" $delay={0.12}>
                <SectionHeading title="What's Not Included" sub="Please note these are outside the scope of this service" />
                {detailLoading ? (
                  <Grid2>
                    {Array.from({ length: 4 }).map((_, i) => (
                      <SkelRow key={i}>
                        <div className="sd-skel" style={{ width: 20, height: 20, borderRadius: '50%', flexShrink: 0 }} />
                        <div className="sd-skel" style={{ height: 14, flex: 1 }} />
                      </SkelRow>
                    ))}
                  </Grid2>
                ) : (
                  <Grid2 className="sd-included-grid">
                    {(exclusions.length > 0 ? exclusions : [
                      'Spare parts or consumables (billed separately)',
                      'Structural or civil/masonry work',
                      'Services outside the booked scope',
                      'Treatment for pre-existing damage or infections',
                    ]).map((item, i) => (
                      <IncludedItem key={i}>
                        <CheckBadge $bg="#f59e0b">
                          <CheckMark>✕</CheckMark>
                        </CheckBadge>
                        <IncludedText $color="#78350f">{item}</IncludedText>
                      </IncludedItem>
                    ))}
                  </Grid2>
                )}
              </SectionCard>

              <SectionCard className="sd-section sd-section-inner" $delay={0.13}>
                <SectionHeading title="Products Used" sub="Premium quality products for best results" />
                {detailLoading ? (
                  <TagRow>
                    {Array.from({ length: 4 }).map((_, i) => (
                      <div key={i} className="sd-skel" style={{ height: 28, width: 90, borderRadius: 20 }} />
                    ))}
                  </TagRow>
                ) : (
                  <TagRow>
                    {(products.length > 0 ? products : [
                      'Eco-friendly cleaner',
                      'Microfiber cloths',
                      'Professional tools',
                      'Branded products',
                    ]).map((p, i) => (
                      <Tag key={i}>
                        {p}
                      </Tag>
                    ))}
                  </TagRow>
                )}
              </SectionCard>

              <SectionCard className="sd-section sd-section-inner" $bg="#f0fdf4" $border="#bbf7d0" $delay={0.13}>
                <SectionHeading title="Before the Service" sub="Please keep these ready" />
                {detailLoading ? (
                  <PrereqList>
                    {Array.from({ length: 3 }).map((_, i) => (
                      <div key={i} className="sd-skel" style={{ height: 14, width: i === 2 ? '60%' : '80%' }} />
                    ))}
                  </PrereqList>
                ) : (
                  <PrereqList>
                    {(prerequisites.length > 0 ? prerequisites : [
                      'Ensure the area to be serviced is accessible',
                      'Remove fragile or valuable items beforehand',
                      'Be available for any questions from the professional',
                    ]).map((p, i) => (
                      <PrereqItem key={i}>
                        <PrereqArrow>→</PrereqArrow>
                        <PrereqText>{p}</PrereqText>
                      </PrereqItem>
                    ))}
                  </PrereqList>
                )}
              </SectionCard>

              <SectionCard className="sd-section sd-section-inner" $delay={0.14}>
                <SectionHeading title="How It Works" sub="Simple 4-step process" />
                <StepsWrap className="sd-steps-wrap">
                  {FALLBACK.steps.map((step, i) => (
                    <React.Fragment key={i}>
                      <StepItem className="sd-step-item">
                        <StepNum $accent={accent}>{i + 1}</StepNum>
                        <div className="sd-step-text">
                          <StepTitle>{step.title}</StepTitle>
                          <StepDesc>{step.desc}</StepDesc>
                        </div>
                      </StepItem>
                      {i < FALLBACK.steps.length - 1 ? (
                        <StepConnector className="sd-step-connector" />
                      ) : null}
                    </React.Fragment>
                  ))}
                </StepsWrap>
              </SectionCard>

              <SectionCard className="sd-section sd-section-inner" $delay={0.15}>
                <SectionHeading title="Our high-impact process" sub="How we deliver spotless, lasting results" />
                <ProcessGrid>
                  {PROCESS_STEPS.map((s, i) => (
                    <ProcessCard key={i}>
                      <ProcessImgWrap>
                        <ProcessImg
                          src={s.img}
                          alt={s.title}
                          onError={e => { e.target.src = PROCESS_FALLBACK_IMG; }}
                        />
                        <ProcessBadge>{s.step}</ProcessBadge>
                      </ProcessImgWrap>
                      <ProcessBody>
                        <ProcessTitle>{s.title}</ProcessTitle>
                        <ProcessDesc>{s.desc}</ProcessDesc>
                      </ProcessBody>
                    </ProcessCard>
                  ))}
                </ProcessGrid>
              </SectionCard>

              <SectionCard className="sd-section sd-section-inner" $delay={0.16}>
                <SectionHeading title="See the difference yourself" sub="Real before & after results from our cleaning jobs" />
                <DiffGrid>
                  {BEFORE_AFTER_ITEMS.map((pair, i) => (
                    <DiffCard key={i}>
                      <DiffHalf $img={pair.before} $side="left" />
                      <DiffHalf $img={pair.after} $side="right" />
                      <DiffTag $side="left">Before</DiffTag>
                      <DiffTag $side="right">After</DiffTag>
                    </DiffCard>
                  ))}
                </DiffGrid>
              </SectionCard>

              <SectionCard className="sd-section sd-section-inner" $delay={0.17}>
                <SectionHeading title="Professional equipment we use" sub="Commercial-grade tools for a superior finish" />
                <EquipGrid>
                  {EQUIPMENT.map((eq, i) => (
                    <EquipCard key={i}>
                      <EquipImg
                        src={eq.img}
                        alt={eq.name}
                        onError={e => { e.target.src = EQUIPMENT_FALLBACK_IMG; }}
                      />
                      <EquipName>{eq.name}</EquipName>
                    </EquipCard>
                  ))}
                </EquipGrid>
              </SectionCard>

              <SectionCard className="sd-section sd-section-inner" $bg="#f0fdf4" $border="#bbf7d0" $delay={0.18}>
                <SectionHeading title="What we'll need from you" sub="Please keep these ready for a smooth service" />
                <ReqGrid>
                  {REQUIREMENTS.map((r, i) => (
                    <ReqCard key={i}>
                      <ReqIconCircle>{r.icon}</ReqIconCircle>
                      <ReqLabel>{r.label}</ReqLabel>
                    </ReqCard>
                  ))}
                </ReqGrid>
              </SectionCard>

              {/* <div className="sd-section sd-section-inner" style={{ background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 14, padding: '22px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)', animationDelay: '0.18s' }}>
                <SectionHeading title="Gallery" sub="See our past work" />
                <div className="sd-gallery-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
                  {FALLBACK.gallery.map((src, i) => (
                    <div key={i} className="sd-gallery-wrap">
                      <img className="sd-gallery-img" src={src} alt={`Gallery ${i + 1}`} onError={e => { e.target.src = FALLBACK.gallery[0]; }} />
                    </div>
                  ))}
                </div>
              </div> */}

              <SectionCard className="sd-section sd-section-inner" $delay={0.22}>
                <SectionHeading title="Customer Reviews" sub="Based on 25,000+ bookings" />
                <ReviewSummary>
                  <ReviewScoreCol>
                    <ReviewScore>4.8</ReviewScore>
                    <Stars rating={5} size={13} />
                    <ReviewOutOf>out of 5</ReviewOutOf>
                  </ReviewScoreCol>
                  <ReviewBars>
                    {[5, 4, 3].map(star => (
                      <ReviewBarRow key={star}>
                        <ReviewBarStarNum>{star}</ReviewBarStarNum>
                        <ReviewBarStar>★</ReviewBarStar>
                        <ReviewBarTrack>
                          <ReviewBarFill $accent={accent} $width={star === 5 ? '78%' : star === 4 ? '16%' : '6%'} />
                        </ReviewBarTrack>
                        <ReviewBarPct>
                          {star === 5 ? '78%' : star === 4 ? '16%' : '6%'}
                        </ReviewBarPct>
                      </ReviewBarRow>
                    ))}
                  </ReviewBars>
                </ReviewSummary>
                <ReviewGrid className="sd-review-grid">
                  {FALLBACK.reviews.map((rev, i) => (
                    <div key={i} className="sd-review">
                      <ReviewHead>
                        <ReviewAuthor>
                          <ReviewAvatar $accent={accent}>
                            {rev.name[0]}
                          </ReviewAvatar>
                          <div>
                            <ReviewName>{rev.name}</ReviewName>
                            <ReviewDate>{rev.date}</ReviewDate>
                          </div>
                        </ReviewAuthor>
                        <Stars rating={rev.rating} size={12} />
                      </ReviewHead>
                      <ReviewText>{rev.text}</ReviewText>
                    </div>
                  ))}
                </ReviewGrid>
              </SectionCard>

              <SectionCard className="sd-section sd-section-inner" $delay={0.26}>
                <SectionHeading title="Frequently Asked Questions" />
                {FALLBACK.faqs.map((faq, i) => (
                  <div key={i} className="sd-faq-item">
                    <button className="sd-faq-btn" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                      <FaqQ>{faq.q}</FaqQ>
                      <FaqToggle $open={openFaq === i} $accent={accent}>+</FaqToggle>
                    </button>
                    <FaqAnswer className="sd-faq-answer" $open={openFaq === i}>
                      <FaqAnswerText>{faq.a}</FaqAnswerText>
                    </FaqAnswer>
                  </div>
                ))}
              </SectionCard>
            </ColLeft>

            {!isStyled && !isKitchenFlow && !isDeepBathroom && !isBathroomFlow && !isRegularPlan && !isNoPlan && (
            <ColRight className="sd-col-right">

              <PlanCard className="sd-section">
                <SectionHeading title="Choose a Plan" sub="Select the option that fits you" />

                {hasMultiplePackages ? (
                  <PkgTabs>
                    {packages.map(pkg => (
                      <button
                        key={pkg.packageID}
                        className="sd-pkg-tab"
                        onClick={() => handlePkgChange(pkg.packageID)}
                        style={{ flex: 1, background: activePkgId === pkg.packageID ? '#ffffff' : 'transparent', color: activePkgId === pkg.packageID ? '#111827' : '#6b7280', boxShadow: activePkgId === pkg.packageID ? '0 1px 4px rgba(0,0,0,0.1)' : 'none' }}
                      >
                        {pkg.packageName}
                      </button>
                    ))}
                  </PkgTabs>
                ) : null}

                {isSvcLoading ? (
                  <ColumnList>
                    {Array.from({ length: 3 }).map((_, i) => (
                      <div key={i} className="sd-skel" style={{ height: 70, borderRadius: 14 }} />
                    ))}
                  </ColumnList>
                ) : (
                  <ColumnList>
                    {displayedSubs.length > 0
                      ? displayedSubs.map((sub, i) => (
                          <SubServiceCard
                            key={sub.subServiceID ?? i}
                            sub={sub}
                            isSelected={selectedSub?.subServiceID === sub.subServiceID}
                            accent={accent}
                            onSelect={() => {
                              setSelectedSubId(sub.subServiceID);
                              if (isBathroomFlow) setBathroomOpen(true);
                              if (isKitchenFlow) setKitchenOpen(true);
                            }}
                          />
                        ))
                      : (
                        <NoPlans>
                          No plans available
                        </NoPlans>
                      )
                    }
                  </ColumnList>
                )}

                {!isStyled && (
                  <BookBtn
                    className="sd-book-btn"
                    onClick={handleBookNow}
                    $booked={booked}
                    $fullWidth
                    $padding="13px"
                  >
                    {booked ? '✓ Booked!' : `Book Now →`}
                  </BookBtn>
                )}

             
               

                
              </PlanCard>


            </ColRight>
            )}
          </TwoCol>
        </OuterPad>
      </Page>

      <div className="sd-sticky-bar">
        <div>
          <StickyLabel>
            {selectedSub?.subServiceName ?? serviceName}
          </StickyLabel>
          <StickyPriceRow>
            <StickyPrice>{activePrice || 'View Plans'}</StickyPrice>
          </StickyPriceRow>
        </div>
        <StickyActions>
          <StickyBackBtn
            className="sd-back-btn"
            onClick={() => navigate(`/${category}/${subCategoryID}`)}
          >
            ← Back
          </StickyBackBtn>
          {!isStyled && (
            <StickyBookBtn
              className="sd-book-btn sd-bar-book"
              onClick={handleBookNow}
              $booked={booked}
            >
              {booked ? '✓ Booked!' : 'Book Now →'}
            </StickyBookBtn>
          )}
        </StickyActions>
      </div>

      <Footer />

      <SelectRequirementsModal
        open={reqOpen}
        onClose={() => setReqOpen(false)}
        ctx={modalCtx}
        destination="/checkout"
        config={reqConfig}
      />
      <SelectRequirementsModal
        open={bathroomOpen}
        onClose={() => setBathroomOpen(false)}
        ctx={modalCtx}
        destination="/cart"
        config={BATHROOM_CONFIG}
      />
      <SelectRequirementsModal
        open={kitchenOpen}
        onClose={() => setKitchenOpen(false)}
        ctx={modalCtx}
        destination="/cart"
        config={KITCHEN_CONFIG}
      />
      <MakeYourOwnPackageModal
        open={pkgOpen}
        onClose={() => setPkgOpen(false)}
        ctx={modalCtx}
      />
    </>
  );
};

export default ServiceDetailPage;
